<template>
  <sub-frame title="公证书操作">
    <mt-button slot="head" class="header-action" @click="openCreate">新增撤销</mt-button>

    <div class="page">
      <mt-search
        v-model="filters.keyword"
        cancel-text="取消"
        placeholder="输入申请人名称或公证书编号"
      ></mt-search>

      <div class="state" v-if="loading">
        <mt-spinner type="fading-circle" :size="26"></mt-spinner>
      </div>
      <div class="state" v-else-if="!certificates.length">暂无撤销公证书</div>
      <div class="certificate-list" v-else>
        <article
          class="certificate-item"
          v-for="item in certificates"
          :key="item.Certificate_Id"
        >
          <div class="certificate-heading">
            <strong>{{ item.Certificate_Number }}</strong>
            <span class="revoked-status">已撤销</span>
          </div>
          <dl class="certificate-details">
            <div>
              <dt>公证事项</dt>
              <dd>{{ item.Notarial_Matter }}</dd>
            </div>
            <div>
              <dt>申请人</dt>
              <dd>{{ applicantNamesText(item.Party_Names) }}</dd>
            </div>            
            <div>
              <dt>出具时间</dt>
              <dd>{{ displayTime(item.Issue_Time) }}</dd>
            </div>
          </dl>
          <div class="certificate-actions">

            <mt-button
              size="small"
              :disabled="actingId === item.Certificate_Id"
              @click="openEdit(item)"
            >
              编辑
            </mt-button>
            <mt-button
              size="small"
              type="danger"
              :disabled="actingId === item.Certificate_Id"
              @click="deleteCertificate(item)"
            >
              删除
            </mt-button>
          </div>
        </article>
      </div>
    </div>

    <!-- 编辑/新增撤销证书弹窗 -->
    <mt-popup v-model="editorVisible" position="bottom" class="editor-popup">
      <div class="editor-heading">
        <strong>{{ editingId ? "编辑撤销" : "新增撤销" }}</strong>
        <button class="close-button" type="button" aria-label="关闭" @click="closeEditor">×</button>
      </div>
      <div class="editor-body">
        <mt-field label="公证书编号" placeholder="请输入公证书编号" v-model.trim="form.Certificate_Number" />
        <mt-field label="公证事项" placeholder="请输入公证事项" v-model.trim="form.Notarial_Matter" />
        <mt-field label="出具时间" type="date" v-model="form.Issue_Time" />
        <mt-field label="申请人" placeholder="多个申请人，中间用空格或者分号隔开" v-model="form.Party_Names" />
      </div>
      <div class="editor-actions">
        <mt-button :disabled="saving" @click="closeEditor">取消</mt-button>
        <mt-button type="danger" :disabled="saving" @click="submitForm">
          {{ saving ? "保存中" : editingId ? "保存修改" : "确认撤销" }}
        </mt-button>
      </div>
    </mt-popup>
  </sub-frame>
</template>

<script>
const EMPTY_FILTERS = {
  keyword: "",
};
const EMPTY_FORM = {
  Certificate_Number: "",
  Notarial_Matter: "商标转让",
  Issue_Time: "",
  Party_Names: "",
};

export default {
  name: "NotarizationNumber",
  data() {
    return {
      filters: { ...EMPTY_FILTERS },
      certificates: [],
      loading: false,
      actingId: null,
      editorVisible: false,
      editingId: null,
      saving: false,
      form: { ...EMPTY_FORM },
    };
  },
  watch: {
    "filters.keyword"() {
      this.loadList();
    },
  },
  created() {
    this.loadList();
  },
  methods: {
    unwrap(response) {
      const payload = response && response.data;
      if (!payload || Number(payload.code) !== 200) {
        const error = new Error(
          (payload && (payload.message || payload.msg)) || "操作失败"
        );
        error.businessError = true;
        throw error;
      }
      return payload.data;
    },

   //错误处理
    showError(error) {
      if (error && error.businessError) {
        this.$toast(error.message);
      }
    },

    //匹配公证书
    matchesKeyword(item) {
      const tokens = this.filters.keyword.trim().split(/[\s,，、;；]+/).map((token) => token.toLowerCase()).filter(Boolean);
      if (!tokens.length) return true;
      const searchable = [item.Certificate_Number, ...(Array.isArray(item.Party_Names) ? item.Party_Names : [])]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return tokens.every((token) => searchable.indexOf(token) !== -1);
    },
    
    //加载公证书列表
    async loadList() {
      this.loading = true;
      try {
        const response = await this.$axios.get("/applycheck/RevokedNotarialCertificate");
        const result = this.unwrap(response);
        const data = Array.isArray(result) ? result : [];
        this.certificates = data.filter((item) => this.matchesKeyword(item));
      } catch (error) {
        this.showError(error);
      } finally {
        this.loading = false;
      }
    },

    //创建公证书
    openCreate() {
      this.editingId = null;
      this.form = { ...EMPTY_FORM, Issue_Time: this.displayTime(new Date()) };
      this.editorVisible = true;
    },
   
    //编辑公证书
    async openEdit(item) {
      this.actingId = item.Certificate_Id;
      try {
        const response = await this.$axios.get(
          `/applycheck/RevokedNotarialCertificate/Detail/${item.Certificate_Id}`
        );
        const detail = this.unwrap(response);
        this.editingId = detail.Certificate_Id;
        this.form = {
          Certificate_Number: detail.Certificate_Number || "",
          Notarial_Matter: detail.Notarial_Matter || "",
          Issue_Time: this.displayTime(detail.Issue_Time),
          Party_Names: this.applicantNamesText(detail.Party_Names, " "),
        };
        this.editorVisible = true;
      } catch (error) {
        this.showError(error);
      } finally {
        this.actingId = null;
      }
    },
    
    //关闭弹窗
    closeEditor() {
      if (!this.saving) {
        this.editorVisible = false;
      }
    },

    //表单校验
    validateForm() {
      if (!this.form.Certificate_Number) {
        this.$toast("请输入公证书编号");
        return null;
      }

      // 中文括号转英文括号
      this.form.Certificate_Number = this.form.Certificate_Number
        .replace(/\uff08/g, "(")
        .replace(/\uff09/g, ")");

      if (this.form.Certificate_Number.length > 100) {
        this.$toast("公证书编号不能超过 100 个字符");
        return null;
      }

      if (!this.form.Notarial_Matter) {
        this.$toast("请输入公证事项");
        return null;
      }

      if (!this.form.Issue_Time) {
        this.$toast("请选择公证书出具时间");
        return null;
      }
      const names = this.applicantNamesToArray(this.form.Party_Names);
      if (!names.length) {
        this.$toast("请至少填写一位申请人");
        return null;
      }
      if (names.some((name) => name.length > 100)) {
        this.$toast("单个申请人姓名不能超过 100 个字符");
        return null;
      }
      return {
        Certificate_Number: this.form.Certificate_Number,
        Notarial_Matter: this.form.Notarial_Matter,
        Issue_Time: new Date(this.form.Issue_Time).toISOString(),
        Party_Names: names,
      };
    },

    //提交表单
    async submitForm() {
      const body = this.validateForm();
      if (!body) return;
      this.saving = true;
      try {
        let response;
        if (this.editingId) {
          response = await this.$axios.post(
            "/applycheck/RevokedNotarialCertificate/Update",
            { ...body, Certificate_Id: this.editingId }
          );
        } else {
          response = await this.$axios.post(
            "/applycheck/RevokedNotarialCertificate/Add",
            body
          );
        }
        this.unwrap(response);
        this.$toast(this.editingId ? "修改成功" : "新增成功");
        this.editorVisible = false;
        await this.loadList();
      } catch (error) {
        this.showError(error);
      } finally {
        this.saving = false;
      }
    },

    //删除撤销证书
    async deleteCertificate(item) {
      try {
        await this.$messagebox.confirm(`确定删除公证书「${item.Certificate_Number}」吗？`);
      } catch (error) {
        return;
      }
      this.actingId = item.Certificate_Id;
      try {
        const response = await this.$axios.post(
          `/applycheck/RevokedNotarialCertificate/Delete/${item.Certificate_Id}`,
          {}
        );
        this.unwrap(response);
        this.certificates = this.certificates.filter(
          (row) => row.Certificate_Id !== item.Certificate_Id
        );
        this.$toast("删除成功");
      } catch (error) {
        this.showError(error);
      } finally {
        this.actingId = null;
        await this.loadList();
      }
    },
    
    //格式化时间
    displayTime(value) {
      if (!value) return "-";
      const p = n => String(n).padStart(2, '0');
      const date = new Date(value);
      return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
    },

    //格式化申请人姓名
    applicantNamesText(value, separator = "、") {
      if (!Array.isArray(value) || !value.length) return "-";
      return value
        .map((name) => String(name).trim().replace(/[、,，;；]+$/, ""))
        .filter(Boolean)
        .join(separator) || "-";
    },

    //解析申请人姓名
    applicantNamesToArray(value) {
      const names = String(value || "").split(/[ ,，、;；]+/).map((name) => name.trim()).filter(Boolean);
      return names.filter((name, index) => names.indexOf(name) === index);
    },

  },
};
</script>

<style scoped lang="scss">
.header-action {
  height: 32px;
  padding: 0 6px;
  border: 0;
  color: #fff;
  background: transparent;
  box-shadow: none;
  font-size: 14px;
}

.page {
  min-height: 100%;
  background: #f5f6f8;

  .mint-search {
    height: 44px;
    overflow: hidden;
  }

  ::v-deep .mint-search-list {
    display: none;
  }
}

.editor-actions,
.certificate-actions,
.certificate-heading,
.list-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.certificate-actions {
  display: flex;
  gap: 8px;
}

.text-button,
.close-button {
  border: 0;
  background: transparent;
  color: #2676c9;
  font-size: 14px;
}

.list-summary {
  padding: 12px;
  color: #666;
  font-size: 13px;
}

.certificate-list {
  padding-bottom: 12px;
}

.certificate-item {
  margin-bottom: 8px;
  padding: 14px 12px;
  background: #fff;
  border-top: 1px solid #eceef0;
  border-bottom: 1px solid #eceef0;
}

.certificate-heading {
  align-items: flex-start;
  gap: 12px;

  strong {
    min-width: 0;
    color: #202124;
    font-size: 15px;
    line-height: 1.5;
    word-break: break-all;
  }
}

.revoked-status {
  flex-shrink: 0;
  padding: 2px 6px;
  border: 1px solid #d95555;
  border-radius: 3px;
  color: #c83d3d;
  font-size: 12px;
}

.certificate-details {
  margin: 12px 0;

  div {
    display: grid;
    grid-template-columns: 72px minmax(0, 1fr);
    margin-top: 7px;
    font-size: 13px;
    line-height: 1.5;
  }

  dt {
    color: #8a8d91;
  }

  dd {
    min-width: 0;
    color: #4a4d52;
    word-break: break-all;
  }
}

.certificate-actions {
  justify-content: flex-end;
  padding-top: 10px;
  border-top: 1px solid #f0f1f2;
}

.state {
  display: flex;
  min-height: 120px;
  align-items: center;
  justify-content: center;
  color: #9a9da2;
  font-size: 14px;
}

.editor-popup {
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  background: #f5f6f8;
}

.editor-heading {
  display: flex;
  min-height: 50px;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
  background: #fff;
  border-bottom: 1px solid #e8eaed;
  font-size: 16px;
}

.close-button {
  width: 36px;
  height: 36px;
  color: #777;
  font-size: 26px;
  line-height: 36px;
}

.editor-body {
  background: #fff;
}

.editor-actions {
  gap: 10px;
  padding: 12px;

  .mint-button {
    flex: 1;
  }
}

</style>
